import { dataAPI } from "./api"
import { TagDTO } from "./models/fields"
import {
    ODataGetArrayResponse,
    ODataGetResourceResponse,
} from "./models/api/data"
import { formatString } from "../util/Strings"
import { RequestErrorDataApi } from "../errors"
import { Exceptions } from "../util/Translations"
import { ITag } from "../components/fields"

const {
    AnErrorOccurredWhileSearchingForTag = 'An error occurred while looking for a tag with the content  ["{0}"] for ["{1}"].',
    AnErrorOccurredWhileFetchingAllTagsFor = 'An error occurred while fetching all tags for ["{0]"].',
} = Exceptions

export function parseTagData(data: TagDTO): ITag {
    return {
        id: data.Id,
        name: data.Label,
        color: data.Color,
    }
}

export const getTagByExactName = async (
    itemId: number,
    fieldId: string,
    filter = "",
): Promise<ITag | null> => {
    return dataAPI
        .getJSON<ODataGetArrayResponse<TagDTO>>(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/TF.TagsItemField/SelectableValues`,
            {
                $filter: `Label eq '${filter}'`,
            },
        )
        .then((response) => {
            return response.value.map(parseTagData)[0]
        })
        .catch(async (error) => {
            if (error instanceof Response) {
                const errorMessage = formatString(
                    AnErrorOccurredWhileSearchingForTag,
                    filter,
                    fieldId,
                )
                throw await RequestErrorDataApi.create(
                    errorMessage,
                    error.status,
                    error,
                )
            }
            throw error
        })
}

interface TagResult {
    tags: ITag[]
    total: number
}

export const getTags = async (
    itemId: number,
    fieldId: string,
    ignoredTags: ITag[],
    includedIds: string[],
    filter = "",
    skip = 0,
    top = 1000,
): Promise<TagResult> => {
    const included = includedIds.length ? ` and Id in (${includedIds})` : ""
    const excluded = ignoredTags.length
        ? ` and not(Id in (${ignoredTags.map((tag) => tag.id)}))`
        : ""
    return dataAPI
        .getJSON<ODataGetArrayResponse<TagDTO>>(
            `ItemFields/ItemId=${itemId},FieldDefinitionId='${fieldId}'/TF.TagsItemField/SelectableValues`,
            {
                $filter: `contains(Label, '${filter}')${included}${excluded}`,
                $top: top,
                $skip: skip,
                $count: true,
            },
        )
        .then((response) => {
            const parsed = response.value.map(parseTagData)
            return {
                tags: parsed,
                total: response["@count"] ?? 0,
            }
        })
        .catch(async (error) => {
            if (error instanceof Response) {
                const errorMessage = formatString(
                    AnErrorOccurredWhileFetchingAllTagsFor,
                    fieldId,
                )
                throw await RequestErrorDataApi.create(
                    errorMessage,
                    error.status,
                    error,
                )
            }
            throw error
        })
}

export const updateTag = async (tag: ITag): Promise<void> => {
    await dataAPI.patchJSON(`Tags/${tag.id}`, {
        Label: tag.name,
        Color: tag.color,
    })
}

export const deleteTag = async (tag: ITag): Promise<void> => {
    await dataAPI.remove(`Tags/${tag.id}`, {
        key: tag.id,
    })
}

export const createTag = async (tag: Partial<ITag>): Promise<ITag> => {
    const result = await dataAPI.postJSON<ODataGetResourceResponse<TagDTO>>(
        "Tags",
        {
            Label: tag.name,
            Color: tag.color,
            isActive: true,
        },
    )

    return parseTagData(result)
}
